import { useCallback, useMemo, useState } from "react";
import { ConnectProto } from "@racoon/api";
import { useLazyGetPickupLocationByIdQuery } from "~/shared/api/pickup-locations/pickup-locations.grpc";
import { useSelectedCompany } from "~/shared/store/company/hooks/useSelectedCompany";
import { useIsOwnOrganizationForWrite } from "~/shared/hooks/use-is-own-organization";
import { formatPickupLocationToForm } from "~/pages/dashboard/pickup-locations/model/formatPickupLocationToForm";
import { PickupLocationModal } from "../ui/pickup-location.modal";

/**
 * Opens a site's card over the page the link was clicked on, so the list the
 * dispatcher is working in stays behind it.
 *
 * The caller renders `pickupLocationCard` once; a row that already holds the
 * whole site passes it to `openLocation`, and one that only knows the id uses
 * `openLocationById`. A site of another organization's list (a zone job's) opens read-only.
 */
export function usePickupLocationCard() {
  const { selectedCompanyId } = useSelectedCompany();
  const [location, setLocation] = useState<ConnectProto.PickupLocation | null>(null);
  const [open, setOpen] = useState(false);
  const [getPickupLocationById] = useLazyGetPickupLocationByIdQuery();
  const isOwnForWrite = useIsOwnOrganizationForWrite();

  const openLocation = useCallback((next: ConnectProto.PickupLocation) => {
    setLocation(next);
    setOpen(true);
  }, []);

  const openLocationById = useCallback(
    async (id: bigint | number | string) => {
      if (!selectedCompanyId) return;
      try {
        const fetched = await getPickupLocationById(
          { id: BigInt(id), serviceProviderId: BigInt(selectedCompanyId) },
          true,
        ).unwrap();
        openLocation(fetched);
      } catch {
        // The global error toaster reports the failure; the page stays as it is.
      }
    },
    [selectedCompanyId, getPickupLocationById, openLocation],
  );

  const locationForm = useMemo(
    () =>
      location ? { ...formatPickupLocationToForm(location), id: Number(location.id) } : undefined,
    [location],
  );

  const pickupLocationCard = (
    <PickupLocationModal
      location={locationForm}
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setLocation(null);
      }}
      hideTrigger
      readOnly={
        !!location &&
        isOwnForWrite(location.ownerOrganizationId, location.serviceProviderId) !== true
      }
    />
  );

  return { openLocation, openLocationById, pickupLocationCard };
}
